<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  ACCESSIBILITY_UI_SETTINGS_KEY,
  createDefaultAccessibilityPreferences,
  normalizeAccessibilityPreferences,
} from './accessibilityPreferences';

const politeMessage = ref('');
const assertiveMessage = ref('');
const isSummarizing = ref(false);
const route = useRoute();
const selectedChat = useMapGetter('getSelectedChat');
const { uiSettings } = useUISettings();

const preferences = ref(
  normalizeAccessibilityPreferences(
    uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY] ||
      createDefaultAccessibilityPreferences()
  )
);

const isAssistiveModeEnabled = computed(
  () =>
    preferences.value.screenReaderMode || preferences.value.keyboardNavigation
);

const replaceLiveMessage = async (target, message) => {
  target.value = '';
  await nextTick();
  target.value = message;
};

const announce = (message, priority = 'polite') => {
  if (!message) return;
  if (priority === 'assertive') politeMessage.value = '';
  replaceLiveMessage(
    priority === 'assertive' ? assertiveMessage : politeMessage,
    message
  );
};

const getContactName = chat =>
  chat?.meta?.sender?.name ||
  chat?.meta?.sender?.available_name ||
  chat?.meta?.sender?.email ||
  'Contato sem nome';

const getConversationBriefing = chat => {
  if (!chat?.id) return 'Nenhuma conversa está aberta.';

  const details = [
    `Conversa ${chat.id} com ${getContactName(chat)}`,
    `status ${chat.status || 'não informado'}`,
  ];

  if (chat.priority) details.push(`prioridade ${chat.priority}`);
  if (chat.unread_count) {
    details.push(
      `${chat.unread_count} ${chat.unread_count === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`
    );
  }

  if (preferences.value.announcementVerbosity === 'detailed') {
    if (chat.meta?.team?.name) details.push(`equipe ${chat.meta.team.name}`);
    if (chat.meta?.assignee?.name) {
      details.push(`atendente ${chat.meta.assignee.name}`);
    }
    if (chat.sla_missed_events?.length) {
      details.push('SLA violado');
    } else if (chat.applied_sla?.id) {
      details.push('SLA ativo');
    }
  }

  return `${details.join('. ')}.`;
};

const focusRegion = (selector, regionName) => {
  const element = document.querySelector(selector);
  if (!element) {
    announce(`${regionName} não está disponível nesta tela.`);
    return;
  }

  element.focus();
  announce(`${regionName}.`);
};

const requestConversationSummary = async () => {
  if (!selectedChat.value?.id) {
    announce('Abra uma conversa antes de solicitar o resumo.');
    return;
  }
  if (isSummarizing.value) {
    announce('O resumo da conversa já está sendo gerado.');
    return;
  }

  try {
    isSummarizing.value = true;
    announce('Gerando resumo da conversa. Aguarde.');
    const { data } = await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/lumerai/tasks/summarize`,
      { conversation_display_id: selectedChat.value.id }
    );
    announce(
      `Resumo da conversa. ${data.summary || data.message}`,
      'assertive'
    );
  } catch (error) {
    announce(
      error.response?.data?.error ||
        'Não foi possível gerar o resumo da conversa.',
      'assertive'
    );
  } finally {
    isSummarizing.value = false;
  }
};

const describeNextAttachment = () => {
  const button = document.querySelector(
    '[data-a11y-describe-image], [data-a11y-describe-attachment]'
  );
  if (!button) {
    announce(
      'Não há imagem ou anexo recebido aguardando descrição nesta conversa.'
    );
    return;
  }
  button.focus();
  button.click();
};

const handleGlobalKeydown = event => {
  if (!preferences.value.keyboardNavigation || event.defaultPrevented) return;
  if (!event.altKey || !event.shiftKey) return;

  const actions = {
    KeyC: () => focusRegion('#conversation-list-region', 'Lista de conversas'),
    KeyM: () => focusRegion('#main-content', 'Conteúdo principal'),
    KeyR: () =>
      focusRegion(
        '#reply-composer [contenteditable="true"], #reply-composer textarea, .reply-box [contenteditable="true"]',
        'Campo de resposta'
      ),
    KeyL: () => announce(getConversationBriefing(selectedChat.value)),
    KeyS: requestConversationSummary,
    KeyI: describeNextAttachment,
  };

  const action = actions[event.code];
  if (!action) return;

  event.preventDefault();
  action();
};

const handleAnnouncement = event => {
  const { message, priority = 'polite' } = event.detail || {};
  announce(message, priority);
};

const handleAccessibilityCommand = event => {
  const action = event.detail?.action;
  const actions = {
    'focus-conversation-list': () =>
      focusRegion('#conversation-list-region', 'Lista de conversas'),
    'focus-main': () => focusRegion('#main-content', 'Conteúdo principal'),
    'focus-reply': () =>
      focusRegion(
        '#reply-composer [contenteditable="true"], #reply-composer textarea, .reply-box [contenteditable="true"]',
        'Campo de resposta'
      ),
    briefing: () => announce(getConversationBriefing(selectedChat.value)),
    summary: requestConversationSummary,
    'describe-image': describeNextAttachment,
  };

  actions[action]?.();
};

const handlePreferencesChanged = event => {
  preferences.value = normalizeAccessibilityPreferences(event.detail);
};

watch(
  () => uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY],
  value => {
    if (value) preferences.value = normalizeAccessibilityPreferences(value);
  },
  { deep: true }
);

watch(
  () => selectedChat.value?.id,
  async (conversationId, previousConversationId) => {
    if (
      !conversationId ||
      conversationId === previousConversationId ||
      !preferences.value.screenReaderMode ||
      !preferences.value.conversationBriefing
    ) {
      return;
    }

    await nextTick();
    announce(getConversationBriefing(selectedChat.value));
  }
);

watch(
  () => selectedChat.value?.messages?.length || 0,
  (messageCount, previousMessageCount) => {
    if (
      !preferences.value.screenReaderMode ||
      !selectedChat.value?.id ||
      messageCount <= previousMessageCount ||
      previousMessageCount === 0
    ) {
      return;
    }

    const lastMessage = selectedChat.value.messages.at(-1);
    if (lastMessage?.message_type === 0) {
      announce(`Nova mensagem de ${getContactName(selectedChat.value)}.`);
    }
  }
);

watch(
  () => selectedChat.value?.status,
  (status, previousStatus) => {
    if (
      !preferences.value.screenReaderMode ||
      !previousStatus ||
      status === previousStatus
    ) {
      return;
    }
    announce(`Conversa alterada para ${status}.`);
  }
);

watch(
  () => selectedChat.value?.sla_missed_events?.length || 0,
  (missedEvents, previousMissedEvents) => {
    if (
      preferences.value.screenReaderMode &&
      preferences.value.announceSla &&
      missedEvents > previousMissedEvents
    ) {
      announce('Atenção. O SLA desta conversa foi violado.', 'assertive');
    }
  }
);

watch(
  () => route.fullPath,
  async () => {
    if (!isAssistiveModeEnabled.value) return;
    await nextTick();
    const heading = document.querySelector(
      '#main-content h1, #main-content h2'
    );
    if (heading?.textContent?.trim()) {
      announce(`Página ${heading.textContent.trim()}.`);
    }
  }
);

onMounted(() => {
  window.addEventListener('a11y-announce', handleAnnouncement);
  window.addEventListener('a11y-command', handleAccessibilityCommand);
  window.addEventListener('a11y-preferences-changed', handlePreferencesChanged);
  window.addEventListener('keydown', handleGlobalKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('a11y-announce', handleAnnouncement);
  window.removeEventListener('a11y-command', handleAccessibilityCommand);
  window.removeEventListener(
    'a11y-preferences-changed',
    handlePreferencesChanged
  );
  window.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<template>
  <div class="sr-only">
    <p role="status" aria-live="polite" aria-atomic="true">
      {{ politeMessage }}
    </p>
    <p role="alert" aria-live="assertive" aria-atomic="true">
      {{ assertiveMessage }}
    </p>
  </div>
</template>
